// =============================================================================
// io/sync/entitlement.ts — read the user's subscription row.
// -----------------------------------------------------------------------------
// Relocated from the old client's `loadEntitlement` (app/index.html:39826).
//
// ── ⚠ WHICH TABLE, AND WHY IT IS NOT THE ONE THE REPO NOTES NAME ──
//
// `public.billing_subscriptions`. The saas repo's own CLAUDE.md calls this table
// "dead code awaiting a Stripe rewrite" — that is STALE. It was created
// provider-neutral (migration 20260722000001), it replaced `polar_subscriptions`
// (20260722000002 drops that table), and the LIVE writer is the Stripe webhook,
// which upserts into it at supabase/functions/stripe-webhook/index.ts:228.
// Measured, not inferred.
//
// There is also a legacy `subscriptions` table in the shared schema. It is not
// the one the webhook writes and it is not what the old client reads.
//
// ── ⚠ RLS DOES THE SCOPING, AND THERE IS NO WRITE POLICY ──
//
// `billing_subscriptions_sel` admits `user_id = auth.uid()` for SELECT only, so
// this read needs no user filter of its own and entitlement cannot be forged
// from a browser. The service-role webhook is the only writer.
//
// ── 🚨 THREE OUTCOMES, NOT TWO ──
//
// A failed read must not look like "no subscription". This returns
// `{ unknown: true }` for a failure and `{ unknown: false, row: null }` for a
// clean read that found nothing, because the gate treats them oppositely: the
// first grants access, the second withholds it. The old client keeps a separate
// `_entitlementUnknown` flag for exactly this and its free-tier suite spends 18
// assertions pinning the distinction.
//
// ⚠ Three attempts before giving up, matching the old client. A single transient
// blip on a cold connection would otherwise put a paying user into the
// fail-open branch — which is safe, but it also means the billing card cannot
// tell them anything true about their own subscription.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { EntitlementRead, EntitlementRow } from '../../domain/entitlement';

/**
 * The access columns, plus the three the billing card needs to describe the
 * subscription rather than guess at it.
 *
 * ⚠ ASKING FOR A COLUMN IS NOT THE SAME AS LETTING IT DECIDE ANYTHING. The
 * access rule takes `EntitlementAccess`, which does not carry tier, status or the
 * cancel flag, so no amount of fetching them can turn a display concern into an
 * access one. That separation used to be a comment; it is a signature now.
 */
const SELECT = 'is_active,current_period_end,revoked_at,tier,status,cancel_at_period_end';

const ATTEMPTS = 3;

interface DbRow {
  is_active?: boolean | null;
  current_period_end?: string | null;
  revoked_at?: string | null;
  tier?: string | null;
  status?: string | null;
  cancel_at_period_end?: boolean | null;
}

function fromDb(r: DbRow): EntitlementRow {
  return {
    isActive: r.is_active === true,
    currentPeriodEnd: r.current_period_end ?? null,
    revokedAt: r.revoked_at ?? null,
    // ⚠ ANYTHING ELSE IS NULL, not 'pro'. The column has no constraint, so a
    // value nobody's purchase code wrote is a value this app has never seen —
    // and the card decides what to call an unknown plan, in one place.
    tier: r.tier === 'founder' || r.tier === 'pro' ? r.tier : null,
    status: r.status ?? null,
    cancelAtPeriodEnd: r.cancel_at_period_end === true,
  };
}

/**
 * The signed-in user's live subscription, or the fact that we could not tell.
 *
 * ⚠ Never throws. A gate that has to be wrapped in a try/catch by every caller
 * is a gate somebody forgets to wrap, and the failure mode of forgetting is a
 * blank screen where a paying customer's analytics should be.
 */
export async function loadEntitlement(sb: SupabaseClient): Promise<EntitlementRead> {
  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    try {
      const { data, error } = await sb
        .from('billing_subscriptions')
        .select(SELECT)
        // ⚠ Filtering on `is_active` here as well as in the rule. A cancelled
        // row and a live one can both exist for one user; newest period first
        // then take one, exactly as the old client does.
        .eq('is_active', true)
        .order('current_period_end', { ascending: false })
        .limit(1);

      if (!error) {
        const row = Array.isArray(data) && data.length ? fromDb(data[0] as DbRow) : null;
        return { unknown: false, row };
      }
    } catch {
      // fall through to the next attempt
    }
  }
  return { unknown: true };
}
